'use client';

import { c as _c } from "react/compiler-runtime";
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useCallback, useEffect, useRef, useState } from 'react';
import { refreshAccessToken } from '@/shared/utils/auth';
import { useAppDispatch } from '@/store/hooks';
import { getStoredWorkspaceId } from '@/shared/utils/workspace';
import { appendMensagem, setTyping } from '../store/slice';
import type { MensagemChatItem, TypingUser } from '../types/arbitralDetailTypes';
const MAX_AUTH_FAILURES = 3;
const MAX_RECONNECT_DELAY_MS = 30000;
interface UseArbitralChatSSEProps {
  processoId: string;
  enabled: boolean;
}
export function useArbitralChatSSE(t0) {
  const $ = _c(15);
  const {
    processoId,
    enabled
  } = t0;
  const dispatch = useAppDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const abortControllerRef = useRef(null);
  const lastEventIdRef = useRef(null);
  const authFailureCountRef = useRef(0);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = abortSignal => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, MAX_RECONNECT_DELAY_MS);
      reconnectAttemptsRef.current = reconnectAttemptsRef.current + 1;
      reconnectTimerRef.current = setTimeout(() => {
        if (!abortSignal.aborted) {
          setReconnectTrigger(_temp);
        }
      }, delay);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const scheduleReconnect = t1;
  let t2;
  if ($[1] !== dispatch || $[2] !== processoId) {
    t2 = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const baseUrl = process.env.NEXT_PUBLIC_API_URL;
      const streamUrl = `${baseUrl}/arbitral/processos/${processoId}/chat/stream`;
      const headers = {
        Accept: "text/event-stream"
      };
      const workspaceId = getStoredWorkspaceId();
      if (workspaceId) {
        headers["X-Workspace-Id"] = workspaceId;
      }
      if (lastEventIdRef.current) {
        headers["Last-Event-ID"] = lastEventIdRef.current;
      }
      try {
        await fetchEventSource(streamUrl, {
          method: "GET",
          headers,
          credentials: "include",
          signal: abortController.signal,
          onopen: async response => {
            if (response.ok && response.headers.get("content-type")?.includes("text/event-stream")) {
              setIsConnected(true);
              authFailureCountRef.current = 0;
              reconnectAttemptsRef.current = 0;
            } else {
              if (response.status === 401) {
                throw new Error("Unauthorized");
              } else {
                if (response.status === 429) {
                  await new Promise(_temp2);
                  throw new Error("Rate limited");
                } else {
                  if (response.status >= 400 && response.status < 500) {
                    throw new Error(`Fatal error: ${response.status}`);
                  }
                }
              }
            }
          },
          onmessage: msg => {
            if (msg.id) {
              lastEventIdRef.current = msg.id;
            }
            if (msg.event === "heartbeat" || msg.event === "connected") {
              return;
            }
            if (msg.event === "nova-mensagem") {
              ;
              try {
                dispatch(appendMensagem(JSON.parse(msg.data) as MensagemChatItem));
              } catch (t3) {
                const err = t3;
                console.warn("[ArbitralChatSSE] Error parsing nova-mensagem:", err);
              }
            } else {
              if (msg.event === "digitando") {
                ;
                try {
                  const {
                    userId,
                    autorNome,
                    autorTipo
                  } = JSON.parse(msg.data) as Pick<TypingUser, 'userId' | 'autorNome' | 'autorTipo'>;
                  dispatch(setTyping({
                    userId,
                    autorNome,
                    autorTipo,
                    timestamp: new Date().toISOString()
                  }));
                } catch (t4) {
                  const err_0 = t4;
                  console.warn("[ArbitralChatSSE] Error parsing digitando:", err_0);
                }
              }
            }
          },
          onclose: () => {
            setIsConnected(false);
            if (!abortController.signal.aborted) {
              scheduleReconnect(abortController.signal);
            }
          },
          onerror: err_1 => {
            setIsConnected(false);
            if (err_1 instanceof Error && err_1.message === "Unauthorized") {
              authFailureCountRef.current = authFailureCountRef.current + 1;
              if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
                throw err_1;
              }
              refreshAccessToken().then(success => {
                if (success) {
                  authFailureCountRef.current = 0;
                  scheduleReconnect(abortController.signal);
                }
              });
              throw err_1;
            }
            scheduleReconnect(abortController.signal);
            throw err_1;
          }
        });
      } catch {
        setIsConnected(false);
      }
    };
    $[1] = dispatch;
    $[2] = processoId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const connect = t2;
  let t3;
  if ($[4] !== connect || $[5] !== enabled || $[6] !== processoId) {
    t3 = () => {
      if (!enabled || !processoId) {
        abortControllerRef.current?.abort();
        setIsConnected(false);
        return;
      }
      connect();
      return () => {
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
        }
        abortControllerRef.current?.abort();
      };
    };
    $[4] = connect;
    $[5] = enabled;
    $[6] = processoId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== connect || $[9] !== enabled || $[10] !== processoId || $[11] !== reconnectTrigger) {
    t4 = [enabled, processoId, connect, reconnectTrigger];
    $[8] = connect;
    $[9] = enabled;
    $[10] = processoId;
    $[11] = reconnectTrigger;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== isConnected) {
    t5 = {
      isConnected
    };
    $[13] = isConnected;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp2(resolve) {
  return setTimeout(resolve, 10000);
}
function _temp(v) {
  return v + 1;
}